import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import useSWR from 'swr';
import { getShipmentsByStatus } from 'services/orders';
import {
  IShipmentByStatusItem,
  IShipmentGroupedByOrder,
  IShipmentsByStatusResponse,
  SHIPMENT_COLUMN_STATUS_ID
} from 'types/orders';
import { optionsParamsSwr } from 'utils/optionsParamsSwr';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';

const MIN_OPERATION_COLUMN_CARDS = 10;
const MAX_SHIPMENT_STATUS_PAGES = 50;

/**
 * Mapea el statusId al nombre de la columna para mostrar.
 * @param statusId - ID del status
 * @returns Nombre del status
 */
const getStatusNameById = (statusId: SHIPMENT_COLUMN_STATUS_ID): string => {
  const statusNames: Record<SHIPMENT_COLUMN_STATUS_ID, string> = {
    [SHIPMENT_COLUMN_STATUS_ID.CONFIRMED]: 'Confirmado',
    [SHIPMENT_COLUMN_STATUS_ID.PREPARATION]: 'Preparación',
    [SHIPMENT_COLUMN_STATUS_ID.TRANSIT_TO_WAREHOUSE]: 'Tránsito a bodega',
    [SHIPMENT_COLUMN_STATUS_ID.IN_WAREHOUSE]: 'En bodega',
    [SHIPMENT_COLUMN_STATUS_ID.TRANSIT_TO_DESTINATION]: 'Tránsito a destino final',
    [SHIPMENT_COLUMN_STATUS_ID.DELIVERY_PROCESS]: 'Proceso de entrega',
    [SHIPMENT_COLUMN_STATUS_ID.DELIVERED]: 'Entregado',
    [SHIPMENT_COLUMN_STATUS_ID.CANCELLED]: 'Cancelado'
  };
  return statusNames[statusId] || '';
};

/**
 * Agrupa los shipments por orderId.
 * @param shipments - Lista de shipments del endpoint by-status
 * @param statusId - ID del status de la columna
 * @returns Lista de shipments agrupados por orden
 */
const groupShipmentsByOrder = (
  shipments: IShipmentByStatusItem[],
  statusId: SHIPMENT_COLUMN_STATUS_ID
): IShipmentGroupedByOrder[] => {
  const groupedByOrderId = new Map<number, IShipmentByStatusItem[]>();

  shipments.forEach(shipment => {
    const orderId = shipment.orderId;
    const existing = groupedByOrderId.get(orderId) || [];
    existing.push(shipment);
    groupedByOrderId.set(orderId, existing);
  });

  const statusName = getStatusNameById(statusId);
  const result: IShipmentGroupedByOrder[] = [];

  groupedByOrderId.forEach(orderShipments => {
    const firstShipment = orderShipments[0];
    const currentStatusId = firstShipment.shipmentStatusLogs?.[0]?.shipmentStatusId ?? statusId;

    result.push({
      orderId: firstShipment.orderId,
      organizationId: firstShipment.order?.organizationId,
      organizationName: firstShipment.order?.organizationName || '',
      destinationCountry: firstShipment.destinationCountry,
      statusId: currentStatusId,
      statusName,
      createdAt: firstShipment.createdAt,
      shipments: orderShipments,
      slaStatus: firstShipment.order?.slaStatus,
      slaDeadline: firstShipment.order?.slaDeadline
    });
  });

  return result;
};

interface UseShipmentsPaginatedOptions {
  statusId: SHIPMENT_COLUMN_STATUS_ID;
  swrKeyPrefix: string;
  queryParams?: string;
  enabled?: boolean;
}

interface UseShipmentsPaginatedReturn {
  items: IShipmentGroupedByOrder[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  total: number;
  loadMore: () => void;
  mutate: () => Promise<void>;
  revalidateFirstPage: () => void;
}

/**
 * Hook base para manejar la carga y paginación de shipments por status.
 * Agrupa automáticamente los shipments por orderId y maneja la acumulación de páginas.
 * @param options - Opciones de configuración del hook
 * @returns Objeto con datos agrupados, estados de carga y funciones de control
 */
const useShipmentsPaginated = ({
  statusId,
  swrKeyPrefix,
  queryParams = '',
  enabled = true
}: UseShipmentsPaginatedOptions): UseShipmentsPaginatedReturn => {
  const [currentPage, setCurrentPage] = useState(1);
  const [accumulatedItems, setAccumulatedItems] = useState<IShipmentByStatusItem[]>([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const autoLoadingRef = useRef(false);
  const dataQueryParamsRef = useRef(queryParams);
  const hasMatchingData = dataQueryParamsRef.current === queryParams;
  const effectivePage = hasMatchingData ? currentPage : 1;

  const swrKey = useMemo(
    () => (enabled ? [swrKeyPrefix, statusId, effectivePage, queryParams] : null),
    [swrKeyPrefix, statusId, effectivePage, queryParams, enabled]
  );

  const { data: response, isLoading, mutate: swrMutate } = useSWR(
    swrKey,
    () => {
      const baseParams = `?statusIds=${statusId}&page=${effectivePage}`;
      const queryParamsProcced = queryParams ? `${baseParams}&${queryParams}` : baseParams;
      return getShipmentsByStatus(queryParamsProcced);
    },
    {
      ...optionsParamsSwr(),
      keepPreviousData: false
    }
  );

  const hasMore = response?.hasMore ?? false;
  const total = response?.total ?? 0;

  const items = useMemo(() => {
    if (!hasMatchingData) return [];
    return groupShipmentsByOrder(accumulatedItems, statusId);
  }, [accumulatedItems, statusId, hasMatchingData]);

  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore || !enabled) return;
    setIsLoadingMore(true);
    setCurrentPage(prev => prev + 1);
  }, [isLoadingMore, hasMore, enabled]);

  const handleMutate = useCallback(async () => {
    if (!enabled) return;
    autoLoadingRef.current = false;
    setCurrentPage(1);
    setIsLoadingMore(false);

    let merged: IShipmentByStatusItem[] = [];
    let page = 1;
    let lastFetchedPage = 1;
    let lastPageResponse: IShipmentsByStatusResponse | null = null;

    try {
      while (page <= MAX_SHIPMENT_STATUS_PAGES) {
        const baseParams = `?statusIds=${statusId}&page=${page}`;
        const fullParams = queryParams ? `${baseParams}&${queryParams}` : baseParams;
        const pageResponse = await getShipmentsByStatus(fullParams);
        if (!pageResponse?.data) break;

        if (page === 1) {
          merged = [...pageResponse.data];
        } else {
          const existingIds = new Set(merged.map(item => item.id));
          const newItems = pageResponse.data.filter(item => !existingIds.has(item.id));
          merged = [...merged, ...newItems];
        }

        lastPageResponse = pageResponse;
        lastFetchedPage = page;
        const hasMoreFromApi = pageResponse.hasMore ?? false;
        const cardsCount = groupShipmentsByOrder(merged, statusId).length;

        if (!hasMoreFromApi) break;
        if (cardsCount >= MIN_OPERATION_COLUMN_CARDS) break;
        page += 1;
      }

      setAccumulatedItems(merged);
      setCurrentPage(lastFetchedPage);

      if (lastPageResponse) {
        await swrMutate(lastPageResponse, { revalidate: false });
      }
    } catch {
      displayErrorNotification();
    }
  }, [statusId, queryParams, swrMutate, enabled]);

  const revalidateFirstPage = useCallback(async () => {
    if (!enabled) return;
    const baseParams = `?statusIds=${statusId}&page=1`;
    const fullParams = queryParams ? `${baseParams}&${queryParams}` : baseParams;

    try {
      const response = await getShipmentsByStatus(fullParams);
      if (!response?.data) return;
      const newPage1Shipments = response.data;

      setAccumulatedItems(prev => {
        const orderIdsToReplace = new Set(newPage1Shipments.map(shipment => shipment.orderId));
        const kept = prev.filter(shipment => !orderIdsToReplace.has(shipment.orderId));
        return [...newPage1Shipments, ...kept];
      });
    } catch {
      displayErrorNotification();
    }
  }, [statusId, queryParams, enabled]);

  const handleResponseUpdate = useCallback(() => {
    if (!response?.data) return;

    const isNewQueryParams = dataQueryParamsRef.current !== queryParams;

    const newShipments = response.data;
    dataQueryParamsRef.current = queryParams;

    setIsLoadingMore(false);
    autoLoadingRef.current = false;

    const shouldReplace = isNewQueryParams || currentPage === 1 || isRefetching;

    if (shouldReplace) {
      if (isNewQueryParams) {
        setCurrentPage(1);
        autoLoadingRef.current = false;
      }
      setAccumulatedItems(newShipments);
      if (isRefetching) setIsRefetching(false);
      return;
    }

    setAccumulatedItems(prev => {
      const existingIds = new Set(prev.map(item => item.id));
      const newItems = newShipments.filter(item => !existingIds.has(item.id));
      return [...prev, ...newItems];
    });
  }, [response, queryParams, currentPage, isRefetching]);

  const handleAutoLoadMore = useCallback(() => {
    if (!hasMatchingData || isLoading || isLoadingMore || autoLoadingRef.current) return;
    if (!response?.hasMore) return;
    if (!response?.data || accumulatedItems.length === 0) return;

    const cardsCount = items.length;
    const needsMoreCards = cardsCount < MIN_OPERATION_COLUMN_CARDS;

    if (needsMoreCards) {
      autoLoadingRef.current = true;
      setIsLoadingMore(true);
      setCurrentPage(prev => prev + 1);
    }
  }, [
    hasMatchingData,
    isLoading,
    isLoadingMore,
    response?.hasMore,
    response?.data,
    accumulatedItems.length,
    items.length
  ]);

  useEffect(() => {
    handleResponseUpdate();
  }, [handleResponseUpdate]);

  useEffect(() => {
    handleAutoLoadMore();
  }, [handleAutoLoadMore]);

  useEffect(() => {
    if (enabled) return;
    setAccumulatedItems([]);
    setCurrentPage(1);
    setIsLoadingMore(false);
    autoLoadingRef.current = false;
    setIsRefetching(false);
  }, [enabled]);

  return {
    items,
    isLoading: isLoading && effectivePage === 1,
    isLoadingMore,
    hasMore,
    total,
    loadMore,
    mutate: handleMutate,
    revalidateFirstPage
  };
};

export default useShipmentsPaginated;
